import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Button, Grid, Paper, Typography } from '@mui/material'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import { NumberFormatter } from '../../../../utility/formatters/NumberFormatter'
import { ProjectStatusChip } from '../../../components/chips/ProjectStatusChip'
import InProgressComponent from '../../../components/ui-widgets/InProgressComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import CurrencyCodeChip from '../../../components/chips/CurrencyCodeChip'
import MUIDataTable, { type MUIDataTableOptions, type SelectableRows } from 'mui-datatables'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'
import { selectSpecificPortfolio, setSpecificPortfolio } from './editPortfoliosSlice'
import {
  useCreateAndProcessSettlementInstructionsMutation,
  useGetSettlementPortfolioQuery
} from '../../../../api-services/asset-holdings/portfolioSettlementsApi'
import { useParams } from 'react-router'
import EditSettlementForm from './EditSettlementForm'
import Refresh from '@mui/icons-material/Refresh'
import COATemplateVersionChip from '../../../components/chips/COATemplateVersionChip'
import EditSettlementProjectForm from './EditSettlementProjectForm'
import PortfolioSettlementProjectStatusChip from '../../../components/chips/PortfolioSettlementProjectStatusChip'
import { StartOutlined } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'

const SettlementPage: React.FC = () => {
  const params = useParams<any>()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const currentSettlementPortfolio: number = useAppSelector<number>(selectSpecificPortfolio)
  let currentPortfolioId: number = 0

  // ================================================================================================================
  // Initialise params
  if (Boolean(params.portfolioId) && currentPortfolioId === 0) {
    currentPortfolioId = params.portfolioId
  }

  const getSettlementPortfolioApiResult = useGetSettlementPortfolioQuery({ portfolioSettlementId: currentPortfolioId })
  const refetchPortfolio = getSettlementPortfolioApiResult.refetch
  const [apiCreateSettlementsTrigger, apiCallResult] = useCreateAndProcessSettlementInstructionsMutation()

  useEffect(() => {
    if (currentSettlementPortfolio !== currentPortfolioId) {
      dispatch(setSpecificPortfolio(currentPortfolioId))
    }
  }, [getSettlementPortfolioApiResult.isSuccess])
  const goTo = (location: string): void => {
    history.push(location)
  }

  const handleRefund = async (): Promise<void> => {
    const choice = window.confirm(
      'Are you sure you want to begin creating refunds for ALL projects?'
    )
    if (choice) {
      await apiCreateSettlementsTrigger({ portfolioSettlementId: currentPortfolioId })
    }
  }

  const handleRefetch = async (): Promise<void> => {
    await refetchPortfolio()
  }

  const canTriggerProcess = (): boolean => {
    return getSettlementPortfolioApiResult.isSuccess && getSettlementPortfolioApiResult.data.state === 'PENDING'
  }

  // ================================================================================================================

  useEffect(() => {
    if (apiCallResult.isSuccess) {
      dispatch(setNotification({
        open: true,
        severity: 'success',
        message: 'The refunds for ALL projects in the portfolio are being created, check back in a few minutes...'
      }))
    }

    if (apiCallResult.isError) {
      dispatch(setNotification({
        open: true,
        severity: 'error',
        message: RtkQueryHelper.mapResponseError(apiCallResult.error)
      }))
    }
  }, [
    apiCallResult.isSuccess, apiCallResult.isError
  ])

  // ================================================================================================================

  const settlementProjectColumns = [
    { name: 'portfolioSettlementProjectId', label: 'Id', options: { filter: false, sort: true } },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <>
              <PortfolioSettlementProjectStatusChip status={value}/>
            </>
          )
        }

      }
    },
    { name: 'projectId', label: 'Project ID', options: { filter: true, sort: true } },
    { name: 'projectName', label: 'Project Name', options: { filter: true, sort: true } },
    {
      name: 'projectStatus',
      label: 'Project Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (<ProjectStatusChip projectStatus={value}/>)
      }
    },
    {
      name: 'saleValueCurrencyId',
      label: 'Sale Currency',
      options: {
        filter: true,
        align: 'right',
        headerAlign: 'center',
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          const sp = getSettlementPortfolioApiResult.isSuccess
            ? getSettlementPortfolioApiResult.data.portfolioSettlementProjects.find((sp) => sp.portfolioSettlementProjectId === tableMeta.rowData[0])
            : null
          return (
            <div>
              <CurrencyCodeChip code={sp?.saleValueCurrencyCode ?? ''} name={sp?.saleValueCurrencyCode ?? ''}/>
            </div>
          )
        }
      }
    },

    {
      name: 'saleValueFormatted',
      label: 'Sale Value',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tm: any, uv: any) => (<div style={{ textAlign: 'right' }}>{value}</div>)
      }
    },
    {
      name: 'calculatedSaleValuePerCell',
      label: 'Sale Value per Cell',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<div style={{ textAlign: 'right' }}>{NumberFormatter.formatWithThousandsSeparator(value, 2)}</div>)
        }
      }
    },
    {
      name: 'energeaAmount',
      label: 'Energea Amount',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<div style={{ textAlign: 'right' }}>{NumberFormatter.formatWithThousandsSeparator(value, 2)}</div>)
        }
      }
    },
    {
      name: 'refundableAmount',
      label: 'Refundable Amount',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<div style={{ textAlign: 'right' }}>{NumberFormatter.formatWithThousandsSeparator(value, 2)}</div>)
        }
      }
    },
    {
      name: 'calculatedRefundPerCell',
      label: 'Refund per Cell',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<div style={{ textAlign: 'right' }}>{NumberFormatter.formatWithThousandsSeparator(value, 2)}</div>)
        }
      }
    },
    {
      name: 'numberCells',
      label: '# Cells',
      options: {
        sort: true,
        customBodyRender: (value: any, tm: any, uv: any) => (<div style={{ textAlign: 'right' }}>{value}</div>)
      }
    },
    {
      name: 'energeaCells',
      label: 'Energea',
      options: {
        sort: true,
        customBodyRender: (value: any, tm: any, uv: any) => (
          <div style={{ textAlign: 'center' }}>
            {parseInt(value) > 0
              ? (
                <><CheckIcon color={'disabled'}/></>
                )
              : (
                <></>
                )
            }
          </div>
        )
      }
    },

    {
      name: 'coaTemplateVersion',
      label: 'COA Version',
      options: {
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <div style={{ textAlign: 'left' }}>
              <COATemplateVersionChip version={value}/>
            </div>
          )
        }
      }
    },
    {
      name: '',
      label: '',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <Button onClick={() => {
              goTo(`/app/finance-area/settlements/project/${tableMeta.rowData[0]}/edit`)
            }} type={'button'} variant={'text'}>
              <StartOutlined/>
            </Button>
          )
        }
      }
    }
  ]

  const optionsSettlementProjects: MUIDataTableOptions = {
    enableNestedDataAccess: '.',
    selectableRowsHideCheckboxes: false,
    selectableRows: 'none' as SelectableRows,
    expandableRows: true,
    expandableRowsOnClick: true,
    rowsPerPage: 100,
    renderExpandableRow: (rowData: string[], rowMeta: any) => {
      if (getSettlementPortfolioApiResult.isSuccess) {
        const settlementProject = getSettlementPortfolioApiResult.data.portfolioSettlementProjects[rowMeta.dataIndex]
        return (
          <tr>
            <td colSpan={16}>
              <Box sx={{ px: 4, pt: 2, pb: 5 }}>
                <EditSettlementProjectForm settlementProject={settlementProject}
                                           triggerRefetch={handleRefetch}/>
              </Box>
            </td>
          </tr>
        )
      }
    }
  }

  // ================================================================================================================

  return (
    <>
      {getSettlementPortfolioApiResult.isLoading && <LoadingComponent/>}
      {(getSettlementPortfolioApiResult.error != null) &&
        <ErrorComponent message={RtkQueryHelper.mapResponseError(getSettlementPortfolioApiResult.error)}/>}
      {getSettlementPortfolioApiResult.isSuccess && (
        <>
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ p: 1, flexGrow: 1 }}>
              <Typography variant={'h5'}>Settlement Portfolio [{getSettlementPortfolioApiResult.data.id}]
                - {getSettlementPortfolioApiResult.data.buyer} ({getSettlementPortfolioApiResult.data.state})</Typography>
            </Box>
            <Box sx={{ p: 1 }}>
              <Button variant={'text'} type="button" onClick={() => {
                goTo('/app/finance-area/settlements')
              }}>
                <ArrowBackIosIcon/>
              </Button>
            </Box>
          </Box>

          <Paper>

            <Box sx={{ px: 4, pt: 1, pb: 4, mt: 3 }}>
              <h3>Portfolio details</h3>

              <Grid container>
                <Grid item md={11}>

                  {(canTriggerProcess() &&
                    <Button variant={'outlined'} startIcon={<ConfirmationNumberIcon/>} type="button" disabled={false}
                            onClick={async () => {
                              await handleRefund()
                            }}>
                      Create Settlement Instructions
                      {(apiCallResult.isLoading && <>&nbsp;&nbsp;&nbsp;<InProgressComponent/></>)}
                    </Button>

                  )}

                </Grid>
                <Grid item md={1} sx={{ textAlign: 'right' }}>
                  <Button variant={'outlined'} type="button" onClick={handleRefetch}>
                    <Refresh/>
                  </Button>

                </Grid>
              </Grid>

              <EditSettlementForm settlement={getSettlementPortfolioApiResult.data}/>

              <MUIDataTable
                title={'Portfolio Settlement Projects'}
                data={getSettlementPortfolioApiResult.data.portfolioSettlementProjects}
                columns={settlementProjectColumns}
                options={optionsSettlementProjects}>
              </MUIDataTable>
            </Box>

          </Paper>
        </>
      )}
    </>
  )
}

export default SettlementPage
